/*
 * Copyright (c)  CYBERKOALA LLC 2025.
 * Proprietary License.
 * Redistribution and use in source and binary forms, with or without
 * modification, are restricted.
 */
import React from 'react';
import { UserPlan } from '@/models';

export const GlowingCardWrapper = ({
                                     admin,
                                     plan,
                                     children,
                                   }: {
  admin: boolean;
  plan?: UserPlan;
  children: React.ReactNode;
}) => {
  if (!plan || plan === UserPlan.FREE) {
    return <>{children}</>;
  }

  const styles: Record<UserPlan, string> = {
    FREE: '',

    BASIC: `
      from-[#4f8cff]
      via-[#72a8ff]
      to-[#6bc8ff]

      dark:from-[#3b82f6]
      dark:via-[#60a5fa]
      dark:to-[#38bdf8]
    `,

    OPTIMIM: `
      from-[#47d97e]
      via-[#63e89d]
      to-[#57ddb7]

      dark:from-[#22c55e]
      dark:via-[#4ade80]
      dark:to-[#34d399]
    `,

    EXTENDED: `
      from-[#bb6cff]
      via-[#df72ff]
      to-[#ff77b7]

      dark:from-[#a855f7]
      dark:via-[#d946ef]
      dark:to-[#ec4899]
    `,
  };

  return (
    <div
      className={`
        group
        relative
        overflow-hidden
        rounded-[24px]
        p-[1px]

        bg-gradient-to-br
        ${styles[plan]}
      `}
    >
      {/* ambient glow blobs */}
      <div
        className="
          absolute
          -top-20
          -left-20
          h-56
          w-56
          rounded-full

          bg-white/25
          dark:bg-white/10

          blur-[80px]
          opacity-50
        "
      />

      <div
        className="
          absolute
          bottom-[-90px]
          right-[-80px]
          h-52
          w-52
          rounded-full

          bg-white/20
          dark:bg-white/5

          blur-[90px]
          opacity-50
        "
      />

      {/* shine only on hover */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="
            absolute
            top-0
            left-[-150%]

            h-full
            w-[40%]

            rotate-12

            bg-gradient-to-r
            from-transparent
            via-white/20
            to-transparent

            opacity-0
            group-hover:opacity-100
          "
        />
      </div>

      {/* card body */}
      <div
        className="
          relative
          z-10
          rounded-[24px]

          border

          border-black/[0.05]
          dark:border-white/[0.06]

          bg-white/90
          dark:bg-[#111214]/90

          before:absolute
          before:inset-0
          before:rounded-[24px]

          before:bg-[linear-gradient(to_bottom,rgba(255,255,255,.65),transparent_25%)]

          dark:before:bg-[linear-gradient(to_bottom,rgba(255,255,255,.06),transparent_35%)]

          before:pointer-events-none
        "
      >
        {children}
      </div>
    </div>
  );
};